import { Box } from '@wix/design-system';
import classnames from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import SearchInput from '../../layouts/SearchInput';
import { CoreDomainSearchModule } from '../../module/module';
import { InlineToolbar } from '../InlineToolbar';
import { ModalToolbar } from '../ModalToolbar';
import s from './DomainSearchBar.scss';
import { getDefaultKeywords } from './DomainSearchBar.utils';
const randomKeywords = getDefaultKeywords();
const TLDS_LIMIT = 6;
const noKeywords = [];
export const DomainSearchBar = _ref => {
  var _keywordsData$keyword;
  let {
    showOptionsIfEmptyInput,
    hideUnassignedDomains,
    onFiltersChange,
    placeholder,
    onSubmit,
    autoFocus,
    compact,
    useModalToolbar,
    onFocus,
    onClear,
    onBlur,
    showSearchIcon,
    submitIcon,
    emphasized,
    hideClearButton,
    hideFilters
  } = _ref;
  const selectedKeywords = CoreDomainSearchModule.SDK.domainInputStore.useKeywords();
  const selectedTlds = CoreDomainSearchModule.SDK.domainInputStore.useTlds();
  const selectedStyle = CoreDomainSearchModule.SDK.domainInputStore.useStyle();
  const {
    SearchContainer,
    StatusContainer,
    StatusComponent
  } = CoreDomainSearchModule.SDK.Layouts;
  const {
    component
  } = CoreDomainSearchModule.internals.dataHooks;
  const {
    t
  } = CoreDomainSearchModule.internals.useTranslations();
  const [isFocused, setIsFocused] = useState(false);
  const keywordsFallback = useMemo(() => randomKeywords(t), [t]);
  useEffect(() => {
    if (isFocused) {
      onFocus == null || onFocus();
    } else {
      onBlur == null || onBlur();
    }
  }, [isFocused, onBlur, onFocus]);
  const {
    setQuery
  } = CoreDomainSearchModule.SDK.domainInputStore;
  const {
    data: keywordsData,
    error: keywordsError
  } = CoreDomainSearchModule.internals.backend.hooks.useGetKeywords({
    limit: 8 - ((selectedKeywords == null ? void 0 : selectedKeywords.length) ?? 0)
  }, true, {
    cacheTime: Infinity,
    staleTime: Infinity,
    useErrorBoundary: false,
    suspense: false,
    retry: false
  });
  const {
    data: tldsData
  } = CoreDomainSearchModule.internals.backend.hooks.useGetTldSelectorData({
    limit: TLDS_LIMIT
  }, true, {
    cacheTime: Infinity,
    staleTime: Infinity,
    useErrorBoundary: false,
    suspense: false,
    retry: false
  });
  const availableKeywords = keywordsError || !(keywordsData != null && (_keywordsData$keyword = keywordsData.keywords) != null && _keywordsData$keyword.length) ? keywordsFallback.keywords ?? noKeywords : keywordsData.keywords ?? noKeywords;
  const concatenatedKeywords = useMemo(() => {
    if (useModalToolbar) {
      // Maintaining stable order
      const availableKeywordSet = new Set(availableKeywords);
      const stableKeywords = [...availableKeywords, ...selectedKeywords.filter(k => !availableKeywordSet.has(k))];
      return stableKeywords;
    }
    const keywordSet = new Set([...(selectedKeywords || []), ...(availableKeywords || [])]);
    return Array.from(keywordSet);
  }, [availableKeywords, selectedKeywords, useModalToolbar]);
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    width: "100%",
    align: "center"
  }, /*#__PURE__*/React.createElement(SearchContainer, {
    dataHook: component,
    className: classnames(s.domainSearchBar, {
      [s.compact]: compact,
      [s.hasFocus]: isFocused,
      [s.emphasized]: emphasized
    })
  }, /*#__PURE__*/React.createElement(SearchInput, {
    showSearchIcon: showSearchIcon,
    submitIcon: submitIcon,
    emphasized: emphasized,
    hideClearButton: hideClearButton,
    autoFocus: autoFocus,
    multiline: useModalToolbar,
    placeholder: placeholder ?? [t('core_domain_search.domain_gate.search_field.placeholder.domain_name'), t('core_domain_search.domain_gate.search_field.placeholder.free_text')],
    showOptionsIfEmptyInput: showOptionsIfEmptyInput,
    hideUnassignedDomains: hideUnassignedDomains,
    onChange: setQuery,
    onClear: onClear,
    onSubmit: filters => {
      const hasActiveFilters = selectedTlds.length > 0 || selectedKeywords.length > 0 || Boolean(selectedStyle);
      if (!useModalToolbar || !hasActiveFilters) {
        setIsFocused(false);
      }
      onSubmit == null || onSubmit(filters);
    },
    onFocus: () => setIsFocused(true)
  }), useModalToolbar ? /*#__PURE__*/React.createElement(ModalToolbar, {
    isFocused: hideFilters ? false : isFocused,
    availableTlds: (tldsData == null ? void 0 : tldsData.tldSelectorData) ?? [],
    concatenatedKeywords: concatenatedKeywords,
    onFiltersChange: onFiltersChange
  }) : /*#__PURE__*/React.createElement(InlineToolbar, {
    compact: compact,
    isFocused: hideFilters ? false : isFocused,
    availableTlds: (tldsData == null ? void 0 : tldsData.tldSelectorData) ?? [],
    concatenatedKeywords: concatenatedKeywords,
    onFiltersChange: onFiltersChange
  })), StatusComponent ? /*#__PURE__*/React.createElement(StatusContainer, null, /*#__PURE__*/React.createElement(StatusComponent, null)) : null);
};
export default DomainSearchBar;
//# sourceMappingURL=DomainSearchBar.js.map